import {
  Alert,
  Button,
  Col,
  Divider,
  Form,
  Input,
  Modal,
  Row,
  Select,
} from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { useUpdateTreuhaenderMutation } from "store/masterdata";
import { UpdateTreuhaenderParams } from "types/masterdata.types";

interface Props {
  initialValues: UpdateTreuhaenderParams;
  visible: boolean;
  onClose(): void;
  onSuccess(): void;
}

function UpdateTreuhanderForm({
  initialValues,
  visible,
  onClose,
  onSuccess,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const [treuhaenderForm] = Form.useForm();

  const [updateTreuhaender, updateTreuhaenderResult] =
    useUpdateTreuhaenderMutation();

  const handleTreuhaenderFormSubmit = (values: UpdateTreuhaenderParams) => {
    const { ...params } = values;
    updateTreuhaender(params);
  };

  const langOptions = [0, 1, 2].map((option) => ({
    label: t(`masterdata.enums.LanguageEnum.${option}`),
    value: option,
  }));

  useEffect(() => {
    if (
      updateTreuhaenderResult.isSuccess &&
      !updateTreuhaenderResult.data?.hasErrors
    ) {
      onSuccess();
      onClose();
    }
  }, [updateTreuhaenderResult]);

  return (
    <Modal
      footer={null}
      title={t("masterdata.lw_details.edit_treuhaender_infos")}
      visible={visible}
      onCancel={onClose}
      destroyOnClose
    >
      <Form
        id="treuhaender-form"
        form={treuhaenderForm}
        onFinish={handleTreuhaenderFormSubmit}
        layout="vertical"
        initialValues={initialValues}
      >
        {updateTreuhaenderResult.data?.hasErrors && (
          <>
            <Alert
              message={updateTreuhaenderResult.data?.errorDetails.errorMessage}
              type="error"
            />
            <Divider />
          </>
        )}
        <Row gutter={20}>
          <Col span={12}>
            <Form.Item name="id" hidden>
              <Input />
            </Form.Item>
            <Form.Item label={t("masterdata.lw_details.th_number")} name="id">
              <Input disabled />
            </Form.Item>
            <Form.Item
              label={t("masterdata.lw_details.lastname")}
              name="lastname"
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label={t("masterdata.lw_details.firstname")}
              name="firstname"
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label={`${t("masterdata.lw_details.phone")} 1`}
              name="primaryPhone"
            >
              <Input />
            </Form.Item>
            <Form.Item
              label={`${t("masterdata.lw_details.phone")} 2`}
              name="phone2"
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={t("masterdata.lw_details.adresse")}
              name="address"
            >
              <Input />
            </Form.Item>
            <Form.Item
              label={t("masterdata.lw_details.postal_code")}
              name="postalCode"
            >
              <Input />
            </Form.Item>
            <Form.Item label={t("masterdata.lw_details.ort")} name="place">
              <Input />
            </Form.Item>
            <Form.Item
              label={t("masterdata.lw_details.email")}
              name="email"
              rules={[{ type: "email" }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="language"
              label={t("masterdata.lw_details.language")}
              rules={[{ required: true }]}
            >
              <Select options={langOptions} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={20}>
          <Col span={12}>
            <Button onClick={onClose} block>
              {t("discard")}
            </Button>
          </Col>
          <Col span={12}>
            <Button
              type="primary"
              htmlType="submit"
              loading={updateTreuhaenderResult.isLoading}
              block
            >
              {t("save_changes")}
            </Button>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}

export default UpdateTreuhanderForm;
